import * as React from "react";
import {useSelector} from "react-redux";
import {assetAllocationSelectedDate} from "./PortfolioMapSelectors";
import {useEffect, useState} from "react";
import {Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps, tooltipClasses } from "@mui/material";
import {PortfolioApi} from "../../api/PortfolioApi";
import {IPlanTreeTargetAllocation} from "../../model/Portfolio.model";
import {
    breakString, getChartHeight,
    getMaxLabelWidth,
    getTargetAllocationsByPeriod
} from "./portfolioUtil";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer, LabelList, TooltipProps,
} from "recharts";
import {AonColors} from "../../../../css/Colors";
import {styled} from "@mui/material/styles";
import {roundNumber} from "../../utils/numberUtil";
import {CustomChartTooltip, ICustomTooltipProps} from "../common/HorizontalBarChart.component";

export interface ICustomYAxisTickProps {
    x: number;
    y: number;
    payload: { value: string };
    index: number;
    targetAllocations: IPlanTreeTargetAllocation[];
    maxNumberOfCharsPerLine: number;
    warningIconPosition: number;
}

export const TARGET_ALLOCATION_DESCRIPTION = "See how the current allocation compares to the investment policy statement targets";
export const LINE_GAP_MULTIPLIER = 1.2;

const WarningTooltip = styled(({ className, ...props }: MuiTooltipProps) => (
    <MuiTooltip {...props} classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        width: "250px",
        backgroundColor: AonColors.ActualWhite,
        border: `1px solid ${AonColors.AonGray05}`,
        borderRadius: "4px",
        boxShadow: `0px 0px 16px ${AonColors.AonGray08}`,
        padding: "12px",
        marginLeft: "10px",
    },
}));

const CustomYAxisTick: React.FunctionComponent<ICustomYAxisTickProps> = (props) => {
    const LABEL_LINE_HEIGHT = 5;
    const MAX_DELTA_MARKET_VS_TARGET = 10;
    const deltaMarketVsTarget = props.targetAllocations[props.index]?.marketValuePctOfTotal
        - props.targetAllocations[props.index]?.targetAllocation;
    const yPosition = props.payload.value.length > props.maxNumberOfCharsPerLine
        ? (roundNumber(props.payload.value.length / props.maxNumberOfCharsPerLine, 0)
            * LABEL_LINE_HEIGHT * -1)
        : LABEL_LINE_HEIGHT;

    return (
        <g transform={`translate(${props.x},${props.y})`}>
            {Math.abs(deltaMarketVsTarget) > MAX_DELTA_MARKET_VS_TARGET && (
                <foreignObject width={20} height={22} x={props.warningIconPosition} y={-15} >
                    <WarningTooltip
                        title={
                            <div>
                                <div className="target-allocation-chart__warning-tooltip-title">Allocation Alert</div>
                                <div className="target-allocation-chart__warning-tooltip-description">
                                    There is more than a 10% difference between current and target allocation
                                </div>
                            </div>
                        }
                    >
                        <div className="fa-light fa-triangle-exclamation target-allocation-chart__yaxis-warning-icon"/>
                    </WarningTooltip>
                </foreignObject>
            )}
            <text textAnchor="end">
                {breakString(props.payload.value, props.maxNumberOfCharsPerLine).map((line, index) => (
                    <tspan key={index} x="0" y={yPosition} dy={`${index * LINE_GAP_MULTIPLIER}em`}
                           className="target-allocation-chart__yaxis-label">
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};

export const TargetAllocationChart: React.FunctionComponent = () => {
    const selectedDate = useSelector(state => assetAllocationSelectedDate(state));
    const [targetAllocations, setTargetAllocations] = useState<IPlanTreeTargetAllocation[]>([]);

    useEffect(() => {
        PortfolioApi.getCurrentPlanPortfolioTargetAllocations()
        .then((response) => {
            setTargetAllocations(response);
        });
    }, []);

    const getCustomTooltip = (tooltipProps: TooltipProps<number, string>) => {
        const {active, payload} = tooltipProps;

        if (active && payload && payload.length) {
            const customTooltipProps: ICustomTooltipProps = {
                title: payload[0].payload.accountName,
                label: `Current Allocation: ${payload[0].value}%`,
                value: `Target Allocation: ${payload[1].value}%`
            };

            return <CustomChartTooltip {...customTooltipProps} />;
        }

        return null;
    };

    const renderChart = (targetAllocations: IPlanTreeTargetAllocation[]) => {
        const MAX_NUMBER_OF_CHARS = 25;
        const MIN_NUMBER_OF_CHARS = 5;
        const maxNumberOfCharsPerLine = Math.min(
            Math.max(...targetAllocations.map(it => it.accountName.length), MIN_NUMBER_OF_CHARS),
            MAX_NUMBER_OF_CHARS
        );
        const maxLabelWidth = getMaxLabelWidth(targetAllocations, maxNumberOfCharsPerLine);
        const chartHeight = getChartHeight(targetAllocations, maxNumberOfCharsPerLine);

        return <div className="target-allocation-chart__chart-container" style={{height: `${chartHeight}px`}}
                    data-testid="target-allocation-chart__chart-container">
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    layout="vertical"
                    data={targetAllocations}
                    margin={{
                        top: 20,
                        right: 70,
                        left: 20,
                        bottom: 5,
                    }}
                    barGap={0}
                >
                    <XAxis type="number" display={"none"} />
                    <YAxis
                        dataKey="accountName"
                        type="category"
                        width={maxLabelWidth}
                        padding={{top:0, bottom:0}}
                        axisLine={false}
                        tickLine={false}
                        tick={
                            (props) => <CustomYAxisTick
                                        {...props}
                                        targetAllocations={targetAllocations}
                                        maxNumberOfCharsPerLine={maxNumberOfCharsPerLine}
                                        warningIconPosition={maxLabelWidth * -1}/>
                        }
                    />

                    <RechartsTooltip content={getCustomTooltip}/>
                    <Legend
                        formatter={(value) => {
                            return (
                                <span className="target-allocation-chart__legend-text">
                                    {value === "marketValuePctOfTotal" ? "Current Allocation" : "Target Allocation"}
                                </span>
                            );
                        }}
                        iconType="circle"
                    />
                    <Bar dataKey="marketValuePctOfTotal" fill={AonColors.AonDataBlue} barSize={30}>
                        <LabelList
                            dataKey="marketValuePctOfTotal"
                            className={"target-allocation-chart__bar-label"}
                            position="right"
                            formatter={(value: string) => `${value}%`}
                        />
                    </Bar>
                    <Bar dataKey="targetAllocation" fill={AonColors.AonTealLight} barSize={30}>
                        <LabelList
                            dataKey="targetAllocation"
                            className={"target-allocation-chart__bar-label"}
                            position="right"
                            formatter={(value: string) => `${value}%`}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>;
    };

    const renderTargetAllocationChart = (targetAllocationsForSelectedPeriod: IPlanTreeTargetAllocation[]) => {
        return <div className="target-allocation-chart__container" data-testid="target-allocation-chart__container">
            <h2>Target Allocation</h2>
            <h4>{TARGET_ALLOCATION_DESCRIPTION}</h4>
            {renderChart(targetAllocationsForSelectedPeriod)}
        </div>;
    };

    const renderComponent = () => {
        const targetAllocationsForSelectedPeriod
            = getTargetAllocationsByPeriod(selectedDate, targetAllocations);
        return targetAllocationsForSelectedPeriod.length > 0
            ? renderTargetAllocationChart(targetAllocationsForSelectedPeriod)
            : null;
    };

    return renderComponent();
};